import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2E7D98', // Medical teal-blue (trust, professionalism)
      light: '#4A9BB5',
      dark: '#1E5A6B',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#7B68A6', // Calming purple (brain health, wisdom)
      light: '#9985C1',
      dark: '#5A4C7A',
      contrastText: '#FFFFFF',
    },
    tertiary: {
      main: '#4A90A4', // Supportive blue-green
      light: '#6BA8BB',
      dark: '#357283',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FAFBFC', // Very light blue-gray for comfort
      paper: '#FFFFFF',
      accent: '#F8F9FA', // Light accent background
    },
    text: {
      primary: '#2C3E50', // Dark blue-gray for excellent readability
      secondary: '#5A6C7D', // Softer secondary text
      disabled: '#9AA5B1',
    },
    error: {
      main: '#D32F2F', // Clear red for important alerts
      light: '#EF5350',
      dark: '#C62828',
    },
    success: {
      main: '#2E7D32', // Medical green for positive outcomes
      light: '#4CAF50',
      dark: '#1B5E20',
    },
    warning: {
      main: '#F57C00', // Warm orange for caution
      light: '#FF9800',
      dark: '#E65100',
    },
    info: {
      main: '#1976D2', // Clear blue for informational content
      light: '#2196F3',
      dark: '#0D47A1',
    },
    // Custom colors for medical context
    medical: {
      brain: '#8E44AD', // Brain/neurology purple
      care: '#27AE60', // Care/healing green
      support: '#3498DB', // Support blue
      gentle: '#E8F4FD', // Gentle background
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(46, 125, 152, 0.08)',
          borderBottom: '1px solid rgba(46, 125, 152, 0.12)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontSize: '0.95rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(46, 125, 152, 0.15)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 12px rgba(46, 125, 152, 0.2)',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: 'rgba(46, 125, 152, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 12px rgba(46, 125, 152, 0.08)',
          border: '1px solid rgba(46, 125, 152, 0.06)',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(46, 125, 152, 0.12)',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.2s ease-in-out',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#FFFFFF',
          boxShadow: '0 1px 8px rgba(46, 125, 152, 0.06)',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(46, 125, 152, 0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(46, 125, 152, 0.10)',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border: '1px solid rgba(46, 125, 152, 0.12)',
          borderRadius: '8px !important',
          boxShadow: '0 1px 4px rgba(46, 125, 152, 0.06)',
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 'auto',
            boxShadow: '0 2px 8px rgba(46, 125, 152, 0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        filled: {
          '&.MuiChip-colorPrimary': {
            backgroundColor: 'rgba(46, 125, 152, 0.1)',
            color: '#2E7D98',
          },
          '&.MuiChip-colorSecondary': {
            backgroundColor: 'rgba(123, 104, 166, 0.1)',
            color: '#7B68A6',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.MuiAlert-standardInfo': {
            backgroundColor: 'rgba(46, 125, 152, 0.1)',
            color: '#2E7D98',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            backgroundColor: '#2E7D98',
            height: 3,
            borderRadius: 2,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          color: '#5A6C7D',
          '&.Mui-selected': {
            color: '#2E7D98',
          },
        },
      },
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      color: '#2C3E50',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      color: '#2C3E50',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: '#2C3E50',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      color: '#2C3E50',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: '#2C3E50',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      color: '#2C3E50',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#2C3E50',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#5A6C7D',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#5A6C7D',
    },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    '0 1px 4px rgba(46, 125, 152, 0.06)',
    '0 2px 8px rgba(46, 125, 152, 0.08)',
    '0 4px 16px rgba(46, 125, 152, 0.10)',
    '0 6px 24px rgba(46, 125, 152, 0.12)',
    '0 8px 32px rgba(46, 125, 152, 0.14)',
    '0 12px 40px rgba(46, 125, 152, 0.16)',
    '0 16px 48px rgba(46, 125, 152, 0.18)',
    '0 20px 56px rgba(46, 125, 152, 0.20)',
    '0 24px 64px rgba(46, 125, 152, 0.22)',
    '0 28px 72px rgba(46, 125, 152, 0.24)',
    '0 32px 80px rgba(46, 125, 152, 0.26)',
    '0 36px 88px rgba(46, 125, 152, 0.28)',
    '0 40px 96px rgba(46, 125, 152, 0.30)',
    '0 44px 104px rgba(46, 125, 152, 0.32)',
    '0 48px 112px rgba(46, 125, 152, 0.34)',
    '0 52px 120px rgba(46, 125, 152, 0.36)',
    '0 56px 128px rgba(46, 125, 152, 0.38)',
    '0 60px 136px rgba(46, 125, 152, 0.40)',
    '0 64px 144px rgba(46, 125, 152, 0.42)',
    '0 68px 152px rgba(46, 125, 152, 0.44)',
    '0 72px 160px rgba(46, 125, 152, 0.46)',
    '0 76px 168px rgba(46, 125, 152, 0.48)',
    '0 80px 176px rgba(46, 125, 152, 0.50)',
    '0 84px 184px rgba(46, 125, 152, 0.52)',
  ],
});

export default theme; 